import React, { useCallback, useEffect } from "react";
import { localStorageRecord } from "src/services/localStorage";
import { isNonEmptyString } from "src/services/predicates";
import { PREFERRED_LANG } from "src/types/localStorageKeys";
import { z } from "zod";
import { UPDATE_PREFERRED_LANGUAGE } from "../plugins/weglot/components/graphql/mutations";
import * as GQL from "../types/graphql";
import { useRemoteDataMutation } from "./useRemoteDataMutation";
import { useTranslationOptions } from "./useTranslationOptions";
import { useUserProfile } from "./useUserProfile";

export type PreferredLanguageSetting = {
  preferredLanguage: string | undefined;
  savePreferredLanguage: (language: string) => void;
};

/**
 * Get preferred language, we will first retrieve preferred language from database,
 * if that doesn't exists then we will use browser's preferred language, and save it to database.
 * We will also save the preferred language when user manually switch the language through language selector dropdown.
 * Note: This hook can only be used in components that are accessed by an authenticated user.
 */
export const usePreferredLanguage: () => PreferredLanguageSetting = () => {
  const ls = localStorageRecord<string>(PREFERRED_LANG, z.string());
  const userProfile = useUserProfile();
  const [update] = useRemoteDataMutation<
    GQL.UpdatePreferredLanguage,
    GQL.UpdatePreferredLanguageVariables
  >(UPDATE_PREFERRED_LANGUAGE);
  const [preferredLanguage, setPreferredLanguage] = React.useState<
    string | undefined
  >(undefined);
  const profile = userProfile.hasData() ? userProfile.data : undefined;
  const profileId = profile?.id;
  const profileLanguage = profile?.preferred_language;
  const translationOption = useTranslationOptions();

  const savePreferredLanguage = React.useCallback(
    (language: string) => {
      ls.setValue(language);
      if (profileId === undefined) {
        return;
      }

      void update({
        variables: {
          person_id: profileId,
          preferred_language: language,
        },
      }).catch((error: unknown) => {
        // Fail silently when the preference cannot be persisted remotely.
        console.error(error);
      });
    },
    [profileId, update, ls]
  );

  React.useEffect(() => {
    if (profileId === undefined || !translationOption.hasData()) {
      return;
    }

    // Order or precedence for preferred language:
    // 1. Preferred language in user profile
    // 2. Stored language from local storage
    // 3. Browser's navigator.language
    if (isNonEmptyString(profileLanguage)) {
      setPreferredLanguage(profileLanguage);
      return;
    }

    const allowedLanguages = translationOption.data.languages;
    const storedLanguage = ls.getValue();
    if (
      isNonEmptyString(storedLanguage) &&
      allowedLanguages.includes(storedLanguage)
    ) {
      setPreferredLanguage(storedLanguage);
      savePreferredLanguage(storedLanguage);
      return;
    }

    const browserPreferredLanguage = toLanguageCode(navigator.language);
    if (
      isNonEmptyString(browserPreferredLanguage) &&
      allowedLanguages.includes(browserPreferredLanguage)
    ) {
      setPreferredLanguage(browserPreferredLanguage);
      savePreferredLanguage(browserPreferredLanguage);
      return;
    }
  }, [
    profileId,
    profileLanguage,
    savePreferredLanguage,
    translationOption,
    ls,
  ]);

  return { preferredLanguage, savePreferredLanguage };
};

/**
 * Use this hook to set preferred language for components that don't require authentication.
 * This needs to be a separate hook from usePreferredLanguage
 * because graphql calls can only be made inside ApolloProvider.
 */
export const usePreferredLanguageBrowserOnly: () => PreferredLanguageSetting =
  () => {
    const ls = localStorageRecord<string>(PREFERRED_LANG, z.string());
    const [preferredLanguage, setPreferredLanguage] = React.useState<
      string | undefined
    >(undefined);
    const translationOption = useTranslationOptions();

    const savePreferredLanguage = useCallback(
      (language: string) => {
        ls.setValue(language);
        setPreferredLanguage(language);
      },
      [setPreferredLanguage, ls]
    );

    useEffect(() => {
      // We can't initialize preferred  language via initial state value since weglot object might still be undefined
      if (!translationOption.hasData()) {
        return;
      }

      const allowedLanguages = translationOption.data.languages;

      // Order or precedence for preferred language:
      // 1. Stored language from local storage
      // 2. Browser's navigator.language
      const storedLanguage = ls.getValue();
      if (
        isNonEmptyString(storedLanguage) &&
        allowedLanguages.includes(storedLanguage)
      ) {
        savePreferredLanguage(storedLanguage);
        return;
      }
      const browserPreferredLanguage = toLanguageCode(navigator.language);
      if (
        isNonEmptyString(browserPreferredLanguage) &&
        allowedLanguages.includes(browserPreferredLanguage)
      ) {
        savePreferredLanguage(browserPreferredLanguage);
        return;
      }
    }, [translationOption, savePreferredLanguage, ls]);

    return { preferredLanguage, savePreferredLanguage };
  };

export function toLanguageCode(language: string): string | undefined {
  // based on list of available languages https://weglot.com/documentation/available-languages/
  let lowerCaseLanguage = language.toLocaleLowerCase().trim();

  // Strip quality values (e.g., "en;q=0.9" -> "en")
  if (lowerCaseLanguage.includes(";")) {
    lowerCaseLanguage = (
      lowerCaseLanguage.split(";")[0] ?? lowerCaseLanguage
    ).trim();
  }

  // Special case for pt-br
  if (lowerCaseLanguage.startsWith("pt-br")) {
    return "pt-br";
  }

  // Take only the first part before hyphen (e.g., "en-US" -> "en")
  const languageCode = (lowerCaseLanguage.split("-")[0] ?? "").trim();

  // Validate: must be 2-3 lowercase letters (language codes are alphabetic)
  if (/^[a-z]{2,3}$/.test(languageCode)) {
    return languageCode;
  }

  return undefined;
}
